'use client';

import React from 'react';
import {
  Code,
  Shield,
  Container,
  Layers,
  Palette,
  Bug,
  Zap,
  Brain,
  Wrench,
  Users,
  Bot,
  Cpu,
  Database,
  Globe,
  Layout,
  Terminal,
  FileCode,
  GitBranch,
  Rocket,
  Settings,
  type LucideIcon,
} from 'lucide-react';
import { PresenceParticipant } from '../endpoints/presence/list_GET.schema';
import { Avatar, AvatarImage, AvatarFallback } from './Avatar';
import styles from './MentionSuggestions.module.css';

const LUCIDE_ICON_MAP: Record<string, LucideIcon> = {
  code: Code,
  shield: Shield,
  container: Container,
  layers: Layers,
  palette: Palette,
  bug: Bug,
  zap: Zap,
  brain: Brain,
  wrench: Wrench,
  users: Users,
  bot: Bot,
  cpu: Cpu,
  database: Database,
  globe: Globe,
  layout: Layout,
  terminal: Terminal,
  'file-code': FileCode,
  'git-branch': GitBranch,
  rocket: Rocket,
  settings: Settings,
};

interface MentionSuggestionsProps {
  suggestions: PresenceParticipant[];
  selectedIndex: number;
  onSelect: (participant: PresenceParticipant) => void;
  onHover: (index: number) => void;
  position?: { top: number; left: number };
}

export const MentionSuggestions: React.FC<MentionSuggestionsProps> = ({
  suggestions,
  selectedIndex,
  onSelect,
  onHover,
  position,
}) => {
  if (suggestions.length === 0) return null;

  const getInitials = (name: string) =>
    name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online':
        return '#22c55e';
      case 'meeting':
        return '#f59e0b';
      case 'away':
        return '#f97316';
      default:
        return '#6b7280';
    }
  };

  return (
    <div
      className={styles.container}
      style={
        position
          ? {
              position: 'absolute',
              bottom: position.top,
              left: position.left,
            }
          : undefined
      }
    >
      <div className={styles.header}>
        <span>Participants</span>
        <span className={styles.hint}>↑↓ to navigate, Tab to select</span>
      </div>
      <ul className={styles.list}>
        {suggestions.map((participant, index) => (
          <li
            key={participant.userId}
            className={`${styles.item} ${index === selectedIndex ? styles.selected : ''}`}
            onClick={() => onSelect(participant)}
            onMouseEnter={() => onHover(index)}
          >
            <div className={styles.avatarWrapper}>
              <Avatar className={styles.avatar}>
                {participant.isAi ? (
                  <AvatarFallback style={{ backgroundColor: participant.color || '#6366f1' }}>
                    {(() => {
                      const iconName = participant.avatarUrl
                        ?.replace('/icons/', '')
                        .replace('.png', '');
                      const IconComp = iconName ? LUCIDE_ICON_MAP[iconName] : null;
                      return IconComp ? (
                        <IconComp size={16} color="#fff" strokeWidth={2} />
                      ) : (
                        <Bot size={16} color="#fff" strokeWidth={2} />
                      );
                    })()}
                  </AvatarFallback>
                ) : (
                  <>
                    {participant.avatarUrl && <AvatarImage src={participant.avatarUrl} />}
                    <AvatarFallback style={{ backgroundColor: participant.color }}>
                      {getInitials(participant.displayName)}
                    </AvatarFallback>
                  </>
                )}
              </Avatar>
              <span
                className={styles.statusDot}
                style={{ backgroundColor: getStatusColor(participant.status) }}
              />
            </div>
            <div className={styles.info}>
              <span className={styles.name}>{participant.displayName}</span>
              {participant.currentFile && (
                <span className={styles.file}>
                  Editing: {participant.currentFile.split('/').pop()}
                </span>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};
